<template>
  <div id="hero">
    <div class="flex flex-col md:flex-row items-center justify-center" style="height: 500px">
      <div class="flex flex-col pl-2 md:pl-0 pr-0 md:pr-52" data-aos="fade-right" data-aos-duration="1000">
        <p class="text-white text-3xl pb-6">My name is</p>

        <p class="title text-6xl font-bold pb-6">José Simões</p>

        <p class="text-white text-3xl">An aspiring Software Engineer, who is about to graduate.</p>
      </div>

      <div class="flex flex-col pl-0 md:pl-52" data-aos="fade-left" data-aos-duration="1000">
        <img class="rounded-full mt-10 md:mt-0 w-40 h-40" src="@/assets/img/jose-simoes-avatar.jpg" alt="José Simões" />
      </div>
    </div>
  </div>

  <div id="about-me" class="pt-10 pb-10" data-aos="fade-up" data-aos-duration="2500">
    <div class="flex flex-col items-center">
      <Title title="Who am I"></Title>
      <div class="w-1/2 leading-relaxed">
        <p>
          I'm a student in the last year of the Computer Science course at the Polytechnic Institute of Santarém. I already have some experience in some high-level programming languages due to what
          I learned during my undergraduate studies - and also due to what I learned through self-exploration and self-learning.
        </p>
        <p class="pt-5">
          I consider myself as a proactive and determined person, and eager to expand my knowledge in the area of Software Engineering.
        </p>
      </div>
      <div class="w-1/2 pt-5">
        <img src="@/assets/img/python-wallpaper.png" alt="Python Wallpaper">
      </div>
    </div>
  </div>

  <div id="backend-stack" class="pt-10" style="background-color: #EFEEEE">
    <Tech title="Backend Stack" :techs="backendTech"></Tech>
  </div>

  <div id="frontend-stack" class="pt-10">
    <Tech title="Frontend Stack" :techs="frontendTech"></Tech>
  </div>

  <div id="projects" class="pt-10 pb-10" style="background-color: #EFEEEE">
    <div class="flex flex-col items-center">
      <Title title="Projects"></Title>
    </div>

    <hr v-if="isMobile()" class="w-5/6 mb-10 m-auto" style="border-bottom: 1px solid rgba(77, 169, 255, 1);"/>

    <div class="flex flex-col">
      <div v-bind:key="project" v-for="project in projects">
        <div class="flex flex-col md:flex-row pt-5 md:pt-10 pb-10 md:pb-20">
          <Project :project="project"></Project>
        </div>
        <hr class="w-5/6 m-auto" style="border-bottom: 1px solid rgba(77, 169, 255, 1);"/>
      </div>
    </div>
  </div>

  <div id="contact-me" class="pt-10">
    <div class="flex flex-col items-center">
      <Title title="Contact Me"></Title>
      <div class="w-1/2 text-center leading-relaxed pb-10">
        <p>
          If you think I fit your company needs, or you simply want to get in touch with me, then fill out the form and let's talk.
        </p>
      </div>
      <form ref="messageForm" @submit.prevent="sendEmail" class="mb-10 w-3/5 md:w-max md:flex md:flex-wrap md:justify-between">
        <div class="flex flex-col mb-4 md:w-1/2">
          <label class="mb-2 tracking-wide text-lg text-blue-500" for="first_name">First Name</label>
          <input v-model="message.firstName" class="border py-2 px-3 focus:outline-none focus:border-blue-500 text-blue-500 md:mr-2" type="text" name="first_name" id="first_name" required>
        </div>
        <div class="flex flex-col mb-4 md:w-1/2">
          <label class="mb-2 text-lg text-blue-500 md:ml-2" for="last_name">Last Name</label>
          <input v-model="message.lastName" class="border py-2 px-3 focus:outline-none focus:border-blue-500 text-blue-500 md:ml-2" type="text" name="last_name" id="last_name" required>
        </div>
        <div class="flex flex-col mb-4 md:w-full">
          <label class="mb-2 text-lg text-blue-500" for="email">Email</label>
          <input v-model="message.email" class="border py-2 px-3 focus:outline-none focus:border-blue-500 text-blue-500" type="email" name="email" id="email" required>
        </div>
        <div class="flex flex-col mb-6 md:w-full">
          <label class="mb-2 text-lg text-blue-500" for="message">Message</label>
          <textarea v-model="message.content" class="border py-2 px-3 focus:outline-none focus:border-blue-500 text-blue-500" name="message" id="message" required />
        </div>
        <button type="submit" class="self-end bg-blue-500 hover:bg-blue-700 rounded text-white font-bold mt-2 py-2 px-4">
          <i class="fas fa-envelope-square mr-2"></i>
          Send a message
        </button>
      </form>
    </div>
  </div>

  <div id="social-media" style="background-color: #EFEEEE">
    <div class="flex flex-col items-center justify-center">
      <div class="flex gap-8 pt-10">
        <a href="https://github.com/jcs99">
          <img class="rounded-full w-8 h-8" src="@/assets/img/github.png" alt="GitHub Icon" />
        </a>
        <a href="https://www.linkedin.com/in/jos%C3%A9-sim%C3%B5es-88255116b">
          <img class="rounded-full w-8 h-8" src="@/assets/img/linkedin.png" alt="Linkedin Icons" />
        </a>
      </div>
      <div class="leading-relaxed mt-10 mb-10">
        <p>Copyright © José Simões</p>
      </div>
    </div>
  </div>
</template>

<script>
import Project from "@/components/Project";
import Title from "@/components/Title";
import Tech from "@/components/Tech";
import axios from "axios"
import AOS from "aos"
import "aos/dist/aos.css"

export default {
  name: 'App',

  components: {
    Project,
    Tech,
    Title
  },

  created() {
    AOS.init();
  },

  setup() {
    return {
      backendTech: {
        'Java': {
          path: require('@/assets/img/java-logo.png'),
          alt: 'Java logo',
          expertise: 'High',
          frameworks: 'Spring, a bit of Java EE',
          testingLibraries: 'JUnit, Mockito'
        },

        'PHP': {
          path: require('@/assets/img/php-logo.png'),
          alt: 'PHP logo',
          expertise: 'Decent',
          frameworks: 'Laravel',
          testingLibraries: 'PHPUnit'
        },

        'Kotlin': {
          path: require('@/assets/img/kotlin-logo.png'),
          alt: 'Kotlin logo',
          expertise: 'Medium',
          frameworks: 'A bit of Android',
          testingLibraries: 'JUnit'
        },

        'C#': {
          path: require('@/assets/img/c-logo.jpg'),
          alt: 'C# logo',
          expertise: 'Low',
          frameworks: 'ASP.net'
        },

        'Python': {
          path: require('@/assets/img/python-logo.png'),
          alt: 'Python logo',
          expertise: 'Low'
        }
      },

      frontendTech: {
        'Javascript': {
          path: require('@/assets/img/javascript-logo.png'),
          alt: 'Javascript logo',
          expertise: 'Comfortable',
          frameworks: 'Angular, Vue, React'
        },

        'HTML': {
          path: require('@/assets/img/html-logo.png'),
          alt: 'HTML logo',
          expertise: 'Medium'
        },

        'CSS': {
          path: require('@/assets/img/css-logo.png'),
          alt: 'CSS logo',
          expertise: 'Basic'
        }
      },

      projects: {
        'gestPAP': {
          title: 'GestPAP',
          description: "GestPAP was a project that I made during the final year of my secondary education. The goal of this project " +
              "was to enable the effortless storage of the students final projects in a digital platform, easily accessible by all the " +
              "other students who could use some inspiration for their own final projects. It was also built to help the teachers keep " +
              "track of all the past final projects.",
          technologiesUsed: 'PHP (Laravel), JQuery',
          link: '#',
          gitHubLink: '#',
          medias: [
            {
              image: require('@/assets/img/gestpap/login.png'),
              title: 'GestPAP',
              content: 'Login page'
            },

            {
              image: require('@/assets/img/gestpap/homepage.png'),
              title: 'GestPAP',
              content: 'Homepage'
            },

            {
              image: require('@/assets/img/gestpap/add-course.png'),
              title: 'GestPAP',
              content: 'Adding a course to the database.'
            },

            {
              image: require('@/assets/img/gestpap/navigate-paps.jpg'),
              title: 'GestPAP',
              content: 'Navigating created projects.'
            }
          ],
        },

        'inqueritoMamaWebApp': {
          title: 'Inquéritos Mamã (Web App)',
          description: "Inquéritos da Mamã was an application being programmed for the Santarém Hospital (Hospital Distrital de Santarém). " +
              "This application was designed to help conduct surveys of the postpartum care service in a more efficient manner, " +
              "with the users of the service evaluating the overall quality of the department. The results would then be collected " +
              "and analyzed by the service administrators with the intent of improving the service.",
          technologiesUsed: 'PHP (Laravel, initially), Vue, Java (Spring Boot, later)',
          link: '#',
          gitHubLink: 'https://github.com/jcs99/LegacyProjects/tree/master/InqueritosMamaPHP',
          medias: [
            {
              image: require('@/assets/img/inqueritos-mama/login.png'),
              title: 'Inquéritos Mamã',
              content: 'Login page'
            },
            {
              image: require('@/assets/img/inqueritos-mama/homepage.png'),
              title: 'Inquéritos Mamã',
              content: 'Homepage'
            },
            {
              image: require('@/assets/img/inqueritos-mama/create-admin.png'),
              title: 'Inquéritos Mamã',
              content: 'Create an admin'
            },
            {
              image: require('@/assets/img/inqueritos-mama/create-patient.png'),
              title: 'Inquéritos Mamã',
              content: 'Create a patient'
            },
            {
              image: require('@/assets/img/inqueritos-mama/inquiry-builder.png'),
              title: 'Inquéritos Mamã',
              content: 'Inquiry builder'
            },
            {
              image: require('@/assets/img/inqueritos-mama/pdf-report.png'),
              title: 'Inquéritos Mamã',
              content: 'PDF report'
            },
          ],
        },

        'inqueritoMamaMobileApp': {
          title: 'Inquéritos Mamã (Mobile App)',
          description: "This was the mobile application of the Inquéritos Mamã, where the users of the postpartum service " +
              "of the Santarém Hospital would answer the surveys created by the service administrators. The results would then be sent " +
              "to the database where they could be analyzed by the web application.",
          technologiesUsed: 'Java (Android)',
          link: '#',
          gitHubLink: 'https://github.com/jcs99/LegacyProjects/tree/master/InqueritosMamaAndroid',
          medias: [
            {
              image: require('@/assets/img/inqueritos-mama/app-homepage.png'),
              title: 'Inquéritos Mamã',
              content: 'Login page'
            },
            {
              image: require('@/assets/img/inqueritos-mama/questionnaire-example.png'),
              title: 'Inquéritos Mamã',
              content: 'Questionnaire'
            },
            {
              image: require('@/assets/img/inqueritos-mama/questionnaire-example2.png'),
              title: 'Inquéritos Mamã',
              content: 'Questionnaire 2'
            }
          ]
        },

        'moodleNotifier': {
          title: 'Moodle Notifier',
          description: 'An Android application that notified users every time changes occurred to the Moodle webpages ' +
              'they decided to monitor. This application was developed mostly to let users know almost immediately when ' +
              'their exam grades came out in the Moodle platform.',
          technologiesUsed: 'Java (Android)',
          link: '#',
          gitHubLink: 'https://github.com/jcs99/MoodleNotifier',
          medias: [
            {
              image: require('@/assets/img/moodle-notifier/moodle-notifier-01.png'),
              title: 'Moodle Notifier',
              content: 'Moodle Notifier 1'
            },
            {
              image: require('@/assets/img/moodle-notifier/moodle-notifier-02.png'),
              title: 'Moodle Notifier',
              content: 'Moodle Notifier 2'
            },
            {
              image: require('@/assets/img/moodle-notifier/moodle-notifier-03.png'),
              title: 'Moodle Notifier',
              content: 'Moodle Notifier 3'
            },
            {
              image: require('@/assets/img/moodle-notifier/moodle-notifier-04.png'),
              title: 'Moodle Notifier',
              content: 'Moodle Notifier 4'
            }
          ]
        },

        'registoHorasBroa': {
          title: 'Registo Horas Broa (Coudelaria Manuel Veiga)',
          description: 'An android application that keeps track of the working hours of a Broa Farm (Quinta da Broa) worker. ' +
              'After the working hours are registered, the details containing the information about the working hours of ' +
              'the worker are stored in a XML file and sent to the farm owner.',
          technologiesUsed: 'Kotlin (Android)',
          link: '#',
          gitHubLink: '#',
          medias: [
            {
              image: require('@/assets/img/coudelaria-veiga/coudelaria-veiga-01.png'),
              title: 'Coudelaria Veiga',
              content: 'Coudelaria Veiga 1'
            },
          ]
        },

        'covid19Tracker': {
          title: 'COVID-19 Tracker',
          description: 'A COVID-19 tracker developed for the Web Systems and Programming Languages courses. The backend ' +
              'consisted of a rest API that provided information about the COVID-19 stats of various countries by fetching ' +
              'and parsing the OurWorldInData CSV file, storing the parsed data in self-balancing trees,' +
              ' and the frontend simply displayed the information of the said stats in both graphical and tabular forms.',
          technologiesUsed: 'Java (Javalin framework), JavaScript',
          link: '#',
          gitHubLink: 'https://github.com/jcs99/COVID19Tracker',
          medias: [
            {
              image: require('@/assets/img/covid-tracker/tracker-01.png'),
              title: 'COVID-19 Tracker',
              content: 'Tracker 1'
            },
            {
              image: require('@/assets/img/covid-tracker/tracker-02.png'),
              title: 'Inquéritos Mamã',
              content: 'Tracker 2'
            },
          ]
        },

        'positiveNewsCovid': {
          title: 'Positive news COVID',
          description: 'An application that used the Google Sentiment Analysis API to analyze COVID-19 news from various ' +
              'news data sources and separate them in positive, neutral, and negative categories. ' +
              'Only news that were considered positive by the Sentiment Analysis API ' +
              'were published to a WordPress blog, with the objective of creating and maintaining a blog ' +
              'dedicated to publishing only positive COVID-19 news.',
          technologiesUsed: 'PHP, WordPress',
          link: '#',
          gitHubLink: 'https://github.com/jcs99/PositiveCOVIDNews',
          medias: [
            {
              image: require('@/assets/img/positive-news-covid/positive-news-covid-01.jpg'),
              title: 'Positive news COVID',
              content: 'Positive news COVID 1'
            },
            {
              image: require('@/assets/img/positive-news-covid/positive-news-covid-02.jpg'),
              title: 'Positive news COVID',
              content: 'Positive news COVID 2'
            },
          ]
        },

        'portugalCovidDailyTable': {
          title: 'Portugal COVID daily table',
          description: 'A web service that shows the latest Portugal COVID data in an organized fashion. The data that ' +
              'is shown by the web service includes new and recovered cases, deaths, hospitalizations, 7-day rolling averages ' +
              'of the most relevant stats, and regional stats. The web service outputs the COVID data in an HTML table, ' +
              'for easy integration in any website.',
          technologiesUsed: 'Java (Spring Boot, webservice), Kotlin (Android)',
          link: '#',
          gitHubLink: 'https://github.com/jcs99/PortugalCovidDailyTable',
          medias: [
            {
              image: require('@/assets/img/portugal-daily-covid-table/covid-table-01.png'),
              title: 'Portugal COVID daily table',
              content: 'Daily table 1'
            },
            {
              image: require('@/assets/img/portugal-daily-covid-table/covid-table-02.png'),
              title: 'Portugal COVID daily table',
              content: 'Daily table 2'
            },
            {
              image: require('@/assets/img/portugal-daily-covid-table/covid-table-03.png'),
              title: 'Portugal COVID daily table',
              content: 'Daily table 3'
            },
            {
              image: require('@/assets/img/portugal-daily-covid-table/covid-table-04.png'),
              title: 'Portugal COVID daily table',
              content: 'Daily table 4'
            }
          ]
        },
      },

      message: {
        firstName: '',
        lastName: '',
        email: '',
        content: ''
      }
    };
  },

  methods: {
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },

    sendEmail() {
      let corsAnywhereLink = "https://secure-springs-87260.herokuapp.com";
      let mailjetLink = "https://api.mailjet.com/v3.1/send";

      let message = {
        "Messages": [
          {
            "From": {
              "Email": "170100228@esg.ipsantarem.pt",
              "Name": "José (Portfolio)"
            },

            "To": [
              {
                "Email": "jose.etpr99@gmail.com"
              }
            ],

            "Subject": this.message.firstName + " " + this.message.lastName + " wants to get in touch with you!",
            "HTMLPart": "Message reads as it follows: <br /><br/ >" + this.message.content + "<br/><br/> Email of the person: "
                + this.message.email
          }
        ]
      }

      let config = {
        headers: {
          'Authorization': 'Basic MGE1NTdhZWY4NjVmMTQyODljYzhhMTRkYTE4ODgyYTg6ODE4M2RmZDlmODc4NDQwMzYwZTllMjY3OGYxOWFiZDk=',
          'Content-Type': 'application/json',
        }
      }

      axios.post(corsAnywhereLink + "/" + mailjetLink, message, config)
        .then(() => {
          this.$toast.success("Your message has been sent.", {
                position: "top",
                duration: 4000,
                max: 1
            }
          )
          this.clearForm();
        })
        .catch(() => this.$toast.error("Your message has not been sent. This was most likely due to a server error. " +
          "Please try again later.", {
            position: "top",
            duration: 4000,
            max: 1
          }
      ))
    },

    clearForm() {
      this.message.firstName = '';
      this.message.lastName = '';
      this.message.email = '';
      this.message.content = '';
      this.$refs.messageForm.reset();
    }
  }
}
</script>

<style>
  body {
    font-family: 'Roboto', sans-serif;
  }

  #hero {
    background-image: url("assets/img/java-code-coding-c.jpg");
  }

  .title {
    color: #4DA9FF;
  }
</style>
